import {
  Kanban,
  UsersThree,
  ChartLineUp,
  ChatCircleText,
  MagnifyingGlass,
  Robot,
} from "@phosphor-icons/react";

const features = [
  {
    icon: Kanban,
    title: "Boards & projects",
    description:
      "Plan work in boards and projects that map to how your team actually ships — not how the tool wishes you did.",
    accent: "from-primary-500 to-primary-400",
    bg: "bg-primary-500/10 dark:bg-primary-500/15",
    iconColor: "text-primary-500",
  },
  {
    icon: UsersThree,
    title: "Real-time collaboration",
    description:
      "Live presence, comments, and mentions keep everyone aligned without another status meeting.",
    accent: "from-[#3B7AE0] to-primary-400",
    bg: "bg-[#3B7AE0]/10 dark:bg-[#3B7AE0]/15",
    iconColor: "text-[#3B7AE0]",
  },
  {
    icon: ChartLineUp,
    title: "Dashboards that earn their space",
    description:
      "Burndown, velocity, and team workload in one place. No spreadsheets, no exports.",
    accent: "from-emerald-500 to-teal-500",
    bg: "bg-emerald-500/10 dark:bg-emerald-500/15",
    iconColor: "text-emerald-500",
  },
  {
    icon: ChatCircleText,
    title: "Conversational task management",
    description:
      "Create, update, and close tasks by chatting with Atlas. It calls the same APIs your team uses, so every change is real.",
    accent: "from-blue-500 to-cyan-500",
    bg: "bg-blue-500/10 dark:bg-blue-500/15",
    iconColor: "text-blue-500",
  },
  {
    icon: MagnifyingGlass,
    title: "Workspace search via Atlas",
    description:
      "Ask Atlas to find tasks, boards, or teammates without clicking through filters and views.",
    accent: "from-violet-500 to-purple-500",
    bg: "bg-violet-500/10 dark:bg-violet-500/15",
    iconColor: "text-violet-500",
  },
  {
    icon: Robot,
    title: "Action items from notes",
    description:
      "Paste a meeting transcript or stand-up notes and Atlas extracts the work into tasks on the right boards.",
    accent: "from-pink-500 to-rose-500",
    bg: "bg-pink-500/10 dark:bg-pink-500/15",
    iconColor: "text-pink-500",
  },
];

export function AIFeaturesSection() {
  return (
    <section id="features" className="relative py-24 lg:py-32">
      <div className="absolute inset-0 hex-grid-bg" />

      <div className="relative mx-auto max-w-7xl px-6">
        {/* Header */}
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary-500/20 dark:border-primary-500/30 bg-primary-500/5 dark:bg-primary-500/10 px-4 py-1.5 text-xs font-semibold tracking-wide text-primary-500 dark:text-primary-400 uppercase">
            <Kanban size={14} weight="fill" />
            Built for shipping
          </div>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl lg:text-5xl">
            Everything your team needs,{" "}
            <span className="gradient-text">in one workspace</span>
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
            Plan work in boards and projects, collaborate in real time, and let
            Atlas handle the busywork.
          </p>
        </div>

        {/* Feature grid */}
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="card-snap group relative rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 p-8"
            >
              {/* Gradient top border on hover */}
              <div
                className={`absolute inset-x-0 top-0 h-0.5 rounded-t-2xl bg-gradient-to-r ${feature.accent} opacity-0 transition-opacity duration-200 group-hover:opacity-100`}
              />

              <div
                className={`inline-flex items-center justify-center rounded-xl ${feature.bg} p-3`}
              >
                <feature.icon
                  size={24}
                  weight="duotone"
                  className={feature.iconColor}
                />
              </div>

              <h3 className="mt-5 text-lg font-semibold text-slate-900 dark:text-white">
                {feature.title}
              </h3>

              <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
